import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "../App";
import QMSLayout from "../components/QMSLayout";
import axios from "axios";
import {
  FileText, Loader2, Plus, CheckCircle2, Clock, AlertTriangle,
  ChevronRight, Eye, Shield, ArrowLeft, Users
} from "lucide-react";

const ax = axios.create({ withCredentials: true });

export default function ScriberDisclosuresPage() {
  const { t } = useTranslation('scriber');
  const { user } = useAuth();
  const [tab, setTab] = useState("templates");
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [adminHistory, setAdminHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await ax.get(`${API}/scriber-disclosures/templates?active_only=false`);
      setTemplates(res.data.templates || []);
    } catch {}
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await ax.get(`${API}/scriber-disclosures/acceptance/history?limit=50`);
      setHistory(res.data.history || []);
    } catch {}
  }, []);

  const loadAdminHistory = useCallback(async () => {
    if (user?.role !== "admin") return;
    try {
      const res = await ax.get(`${API}/scriber-disclosures/acceptance/admin-history?limit=100`);
      setAdminHistory(res.data.history || []);
    } catch {}
  }, [user]);

  useEffect(() => {
    Promise.all([loadTemplates(), loadHistory(), loadAdminHistory()]).then(() => setLoading(false));
  }, [loadTemplates, loadHistory, loadAdminHistory]);

  const createTemplate = async (data) => {
    try {
      await ax.post(`${API}/scriber-disclosures/templates`, data);
      setShowNew(false);
      loadTemplates();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to create template");
    }
  };

  const toggleActive = async (templateId, active) => {
    try {
      await ax.put(`${API}/scriber-disclosures/templates/${templateId}`, { active: !active });
      loadTemplates();
    } catch {}
  };

  if (loading) {
    return (
      <QMSLayout title={t('scriber:title')}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#36A9E1] animate-spin" />
        </div>
      </QMSLayout>
    );
  }

  // Template detail
  if (selectedTemplate) {
    return (
      <QMSLayout title={t('scriber:title')}>
        <div className="space-y-4" data-testid="disclosure-template-detail">
          <button onClick={() => setSelectedTemplate(null)} className="flex items-center gap-2 text-[#8B95A5] hover:text-white text-sm" data-testid="back-from-template">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="bg-[#181B23] rounded-lg p-5 border border-[#2A2D38]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold text-lg">{selectedTemplate.title}</h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#8B95A5]">v{selectedTemplate.version}</span>
                <span className={`px-2 py-1 rounded text-xs ${selectedTemplate.active ? "bg-emerald-400/10 text-emerald-400" : "bg-red-400/10 text-red-400"}`}>
                  {selectedTemplate.active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
            <div className="text-sm text-[#8B95A5] mb-3">
              <span className="text-white">Workflow:</span> {selectedTemplate.workflow_type} &middot;
              <span className="text-white ml-2">Requires Acceptance:</span> {selectedTemplate.requires_acceptance ? "Yes" : "No"}
            </div>
            <div className="bg-[#0D0F14] rounded p-4 text-sm text-[#CCD6E0] whitespace-pre-wrap" data-testid="disclosure-body">
              {selectedTemplate.body}
            </div>
            {user?.role === "admin" && (
              <div className="flex gap-2 mt-4">
                <button onClick={() => toggleActive(selectedTemplate.template_id, selectedTemplate.active)}
                  className={`px-3 py-2 rounded text-sm ${selectedTemplate.active ? "bg-red-400/10 text-red-400 hover:bg-red-400/20" : "bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20"}`}
                  data-testid="toggle-active-btn"
                >{selectedTemplate.active ? "Deactivate" : "Activate"}</button>
              </div>
            )}
          </div>
        </div>
      </QMSLayout>
    );
  }

  const tabs = [
    { id: "templates", label: "Templates", icon: FileText },
    { id: "my-history", label: "My Acceptances", icon: CheckCircle2 },
    ...(user?.role === "admin" ? [{ id: "admin-history", label: "All Acceptances", icon: Users }] : []),
  ];

  return (
    <QMSLayout title={t('scriber:title')}>
      <div className="space-y-4" data-testid="disclosures-page">
        <div className="flex items-center justify-between">
          <div className="flex gap-1 bg-[#181B23] rounded-lg p-1" data-testid="disclosures-tabs">
            {tabs.map(tabItem => {
              const Icon = tabItem.icon;
              return (
                <button key={tabItem.id} onClick={() => setTab(tabItem.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded text-sm ${tab === tabItem.id ? "bg-[#36A9E1]/10 text-[#36A9E1]" : "text-[#8B95A5] hover:text-white"}`}
                  data-testid={`tab-${tabItem.id}`}
                ><Icon className="w-4 h-4" />{tabItem.label}</button>
              );
            })}
          </div>
          {tab === "templates" && user?.role === "admin" && (
            <button onClick={() => setShowNew(true)} className="px-3 py-2 rounded bg-[#36A9E1] text-white text-sm flex items-center gap-2 hover:bg-[#2B8BC2]" data-testid="new-template-btn">
              <Plus className="w-4 h-4" /> New Template
            </button>
          )}
        </div>

        {/* Templates List */}
        {tab === "templates" && (
          <div className="space-y-2" data-testid="templates-list">
            {templates.length === 0 ? (
              <div className="bg-[#181B23] rounded-lg p-8 text-center text-[#8B95A5]">
                <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No disclosure templates yet.</p>
              </div>
            ) : templates.map(tmpl => (
              <div key={t.template_id} onClick={() => setSelectedTemplate(t)}
                className="bg-[#181B23] rounded-lg p-4 border border-[#2A2D38] hover:border-[#36A9E1]/30 cursor-pointer"
                data-testid={`template-${t.template_id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium text-sm">{t.title}</div>
                    <div className="text-xs text-[#8B95A5] mt-1">{t.workflow_type} &middot; v{t.version} &middot; {t.requires_acceptance ? "Acceptance required" : "Informational"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${t.active ? "bg-emerald-400/10 text-emerald-400" : "bg-slate-400/10 text-slate-400"}`}>
                      {t.active ? "Active" : "Inactive"}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#8B95A5]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* My History */}
        {tab === "my-history" && (
          <div className="space-y-2" data-testid="my-acceptance-history">
            {history.length === 0 ? (
              <div className="bg-[#181B23] rounded-lg p-8 text-center text-[#8B95A5]">No acceptance records.</div>
            ) : history.map(h => (
              <div key={h.acceptance_id} className="bg-[#181B23] rounded-lg p-3 border border-[#2A2D38]">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white text-sm">{h.disclosure_id} &middot; v{h.version}</div>
                    <div className="text-xs text-[#8B95A5] mt-1">{h.workflow_type} &middot; {new Date(h.accepted_at).toLocaleString()}</div>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Admin History */}
        {tab === "admin-history" && (
          <div className="space-y-2" data-testid="admin-acceptance-history">
            {adminHistory.length === 0 ? (
              <div className="bg-[#181B23] rounded-lg p-8 text-center text-[#8B95A5]">No acceptance records system-wide.</div>
            ) : adminHistory.map(h => (
              <div key={h.acceptance_id} className="bg-[#181B23] rounded-lg p-3 border border-[#2A2D38]">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white text-sm">{h.user_name || h.user_id} — {h.disclosure_id} v{h.version}</div>
                    <div className="text-xs text-[#8B95A5] mt-1">{h.workflow_type} &middot; {new Date(h.accepted_at).toLocaleString()}</div>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* New Template Modal */}
        {showNew && <NewTemplateModal onSubmit={createTemplate} onClose={() => setShowNew(false)} />}
      </div>
    </QMSLayout>
  );
}

function NewTemplateModal({ onSubmit, onClose }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [workflowType, setWorkflowType] = useState("general");
  const [version, setVersion] = useState("1.0");
  const [requiresAcceptance, setRequiresAcceptance] = useState(true);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="new-template-modal">
      <div className="bg-[#181B23] rounded-xl border border-[#2A2D38] w-full max-w-lg p-6">
        <h2 className="text-white font-bold text-lg mb-4">New Disclosure Template</h2>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="template-title-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Workflow Type</label>
            <select value={workflowType} onChange={e => setWorkflowType(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="workflow-type-select">
              {["general", "scriber_session", "audit_conduct", "evidence_capture", "voice_input", "redaction"].map(w => (
                <option key={w} value={w}>{w.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Version</label>
            <input value={version} onChange={e => setVersion(e.target.value)} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="template-version-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B95A5] block mb-1">Disclosure Body</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={5} className="w-full bg-[#0D0F14] border border-[#2A2D38] rounded px-3 py-2 text-white text-sm" data-testid="template-body-input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#8B95A5]">
            <input type="checkbox" checked={requiresAcceptance} onChange={e => setRequiresAcceptance(e.target.checked)} className="rounded" data-testid="requires-acceptance-check" />
            Requires Acceptance
          </label>
        </div>
        <div className="flex gap-3 mt-5 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded bg-[#1F222D] text-[#8B95A5] hover:bg-[#2A2D38] text-sm">{t('common:actions.cancel')}</button>
          <button onClick={() => onSubmit({ title, body, workflow_type: workflowType, version, requires_acceptance: requiresAcceptance })}
            disabled={!title || !body}
            className="px-4 py-2 rounded bg-[#36A9E1] text-white text-sm hover:bg-[#2B8BC2] disabled:opacity-40"
            data-testid="create-template-submit"
          >Create Template</button>
        </div>
      </div>
    </div>
  );
}
